// ✅ src/pages/VehicleV2/LubricationStockPositionReport_Print.jsx
// FULL PRINT PAGE for: LubricationStockPositionReportPrint.aspx (Crystal print)
//
// ✅ Works with your LIST PAGE (LubeStockPositionReport.jsx) print redirect using querystring.
//
// Querystring expected (we pass from list page):
//   branchId
//   month
//   year
//   itemcategoryid
//   itemname
//   supplier        (this is suppliersCsv: "A,B,C," OR "%" )
//   date            (usually "")
//
// ✅ Backend used (same module as list page):
//   GET  /api/lubeStockPositionRpt/list
//
// Backend list params it expects:
//   branchId, month, year, itemCategoryId, itemId, suppliersCsv, date
//
// Response:
//   { success:true, rows:[...], columns:[...] }
//
// NOTE:
// - This print page calls /list to fetch rows (simple + consistent).
// - Uses your template header/footer and landscape A4 print CSS.
// - Renders dynamic columns (either from res.data.columns or inferred from first row).

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/lubeStockPositionRpt";

const toStr = (v, d = "") => (v === null || v === undefined ? d : String(v)).trim();
const safe = (v, fallback = "") => {
  const s = toStr(v, "");
  return s ? s : fallback;
};

const formatKey = (k) =>
  String(k || "")
    .replace(/^fld_/i, "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function LubricationStockPositionReport_Print() {
  const navigate = useNavigate();
  const { search } = useLocation();

  // ✅ read query params (same idea as old Session keys)
  const params = useMemo(() => {
    const qs = new URLSearchParams(search);

    const branch = qs.get("branch") ?? qs.get("branchId") ?? qs.get("branchValue") ?? "Select";
    const month = qs.get("month") ?? "Select";
    const year = qs.get("year") ?? "Select";

    const itemcategoryid =
      qs.get("itemcategoryid") ?? qs.get("itemCategoryId") ?? qs.get("itemcategory") ?? "";

    const itemname =
      qs.get("itemname") ?? qs.get("itemId") ?? qs.get("itemName") ?? "Select";

    // list page sends suppliersCsv in supplier param (csv with trailing comma)
    const supplier = qs.get("supplier") ?? qs.get("suppliersCsv") ?? "%";

    const date = qs.get("date") ?? "";

    return {
      branch: safe(branch, "Select"),
      month: safe(month, "Select"),
      year: safe(year, "Select"),
      itemcategoryid: safe(itemcategoryid, ""),
      itemname: safe(itemname, "Select"),
      supplier: safe(supplier, "%"),
      date: safe(date, ""),
    };
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [serverColumns, setServerColumns] = useState([]);

  const columns = useMemo(() => {
    // Prefer backend columns, else infer from first row
    const colsFromApi = (serverColumns || []).filter(Boolean);
    if (colsFromApi.length) return colsFromApi;

    if (!rows || rows.length === 0) return [];
    const keys = Object.keys(rows[0] || {});
    // sort stable: fld_* first then alpha
    keys.sort((a, b) => {
      const af = a.toLowerCase().startsWith("fld_") ? 0 : 1;
      const bf = b.toLowerCase().startsWith("fld_") ? 0 : 1;
      if (af !== bf) return af - bf;
      return a.localeCompare(b);
    });
    return keys;
  }, [rows, serverColumns]);

  const fetchPrintRows = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          branchId: params.branch,
          month: params.month,
          year: params.year,
          itemCategoryId: params.itemcategoryid,
          itemId: params.itemname,
          suppliersCsv: params.supplier, // csv (A,B,) or "%"
          date: params.date || "",
        },
      });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed to load print data");

      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setServerColumns(Array.isArray(res.data?.columns) ? res.data.columns : []);
    } catch (e) {
      setRows([]);
      setServerColumns([]);
      alert(e?.response?.data?.message || e.message || "Failed to load print data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.branch,
    params.month,
    params.year,
    params.itemcategoryid,
    params.itemname,
    params.supplier,
    params.date,
  ]);

  const handlePrint = () => window.print();

  const reportTitle = "Lubrication Stock Position Report";

  // For filter display: show supplier names nicely (remove trailing comma)
  const supplierDisplay = useMemo(() => {
    const s = toStr(params.supplier, "%");
    if (!s || s === "%") return "All";
    return s.replace(/,+\s*$/, ""); // remove trailing comma(s)
  }, [params.supplier]);

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden in print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors"
          disabled={loading}
        >
          {loading ? "Loading..." : "Print"}
        </button>
      </div>

      <div className="max-w-7xl mx-auto border-2 border-black p-3 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header */}
          <div className="border border-black p-3 mb-3 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
              <div className="text-[11px] leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          {/* Screen Code */}
          <div className="flex justify-end mb-2">
            <div className="text-[11px] font-semibold">INFRA SCREEN : B-63/1</div>
          </div>

          <div className="text-center text-base font-bold my-3">{reportTitle}</div>

          {/* Filter summary */}
          <div className="border border-black p-2 mb-3 text-[11px]">
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <div>
                <span className="font-semibold">Branch:</span> {params.branch}
              </div>
              <div>
                <span className="font-semibold">Month:</span> {params.month}
              </div>
              <div>
                <span className="font-semibold">Year:</span> {params.year}
              </div>
              <div>
                <span className="font-semibold">Category:</span> {params.itemcategoryid || "All"}
              </div>
              <div>
                <span className="font-semibold">Item:</span> {params.itemname || "All"}
              </div>
              <div className="min-w-[240px]">
                <span className="font-semibold">Supplier:</span> {supplierDisplay}
              </div>
              <div>
                <span className="font-semibold">Date:</span> {params.date || ""}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-black text-[10px]">
              <thead>
                <tr>
                  <th className="border border-black p-1 text-center font-bold w-[50px]">S. No</th>
                  {columns.map((c) => (
                    <th key={c} className="border border-black p-1 text-center font-bold whitespace-nowrap">
                      {formatKey(c)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows && rows.length > 0 ? (
                  rows.map((r, idx) => (
                    <tr key={idx}>
                      <td className="border border-black p-1 text-center">{idx + 1}</td>
                      {columns.map((c) => (
                        <td key={c} className="border border-black p-1 text-center whitespace-nowrap">
                          {safe(r?.[c], "")}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="border border-black p-2 text-center"
                      colSpan={1 + (columns.length || 1)}
                    >
                      {loading ? "Loading..." : "No record found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="mt-3 p-2 border border-black bg-gray-100 text-[11px]">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style jsx>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 8mm;
          }
          body {
            margin: 0;
            padding: 0;
          }
          .print\\:hidden {
            display: none !important;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          tr {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}